'use client';

import { memo, useCallback, useMemo } from 'react';

import { type FullWorkspace } from '@air/api/types';
import { CheckIcon, ChevronDownIcon, LogOutIcon } from '@air/design-icons';
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';

export type WorkspaceSelectorProps = {
  workspaces: FullWorkspace[];
  selectedWorkspace: FullWorkspace | undefined;
  onWorkspaceChange: (workspaceId: string) => void;
  /** Handler for logout action - if not provided, logout option won't be shown */
  onLogout?: () => void;
  /** Show loading skeleton */
  isLoading?: boolean;
};

/** Workspace avatar - shows image or initials with colored background */
const WorkspaceAvatar = memo(({ workspace }: { workspace: FullWorkspace }) => {
  // Compute initials for fallback (must be before early return per hooks rules)
  const initials = useMemo(
    () =>
      workspace.name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
    [workspace.name],
  );

  // Build optimized image URL with imgix parameters if applicable
  const imageSrc = useMemo(() => {
    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (!workspace.image) return undefined;

    // Only append imgix parameters if the URL is from imgix
    if (workspace.image.includes('imgix.net')) {
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

      return `${workspace.image}?h=48&w=48&fit=crop&dpr=${dpr}`;
    }

    return workspace.image;
  }, [workspace.image]);

  if (imageSrc) {
    return (
      <div className="size-6 shrink-0 overflow-hidden rounded">
        {/* biome-ignore lint/performance/noImgElement: External CDN image with query params. */}
        <img alt={workspace.name} className="size-full object-cover" src={imageSrc} />
      </div>
    );
  }

  return (
    <div className="flex size-6 shrink-0 items-center justify-center rounded bg-neutral-8 font-medium text-11 text-white">
      {initials}
    </div>
  );
});

WorkspaceAvatar.displayName = 'WorkspaceAvatar';

/** Skeleton placeholder while workspace is loading */
const WorkspaceSelectorSkeleton = memo(() => (
  <div className="flex w-full min-w-0 items-center justify-between gap-2 rounded-6 py-1 pr-2 pl-1">
    <div className="flex min-w-0 items-center gap-2">
      <div className="size-6 animate-pulse rounded bg-neutral-4" />
      <div className="h-4 w-24 animate-pulse rounded bg-neutral-4" />
    </div>
    <ChevronDownIcon className="shrink-0 text-neutral-6" size="sm" />
  </div>
));

WorkspaceSelectorSkeleton.displayName = 'WorkspaceSelectorSkeleton';

export const WorkspaceSelector = memo(
  ({ workspaces, selectedWorkspace, onWorkspaceChange, onLogout, isLoading }: WorkspaceSelectorProps) => {
    const handleSelect = useCallback(
      (workspaceId: string) => {
        onWorkspaceChange(workspaceId);
      },
      [onWorkspaceChange],
    );

    if (isLoading) {
      return <WorkspaceSelectorSkeleton />;
    }

    return (
      <DropdownMenu
        align="start"
        sideOffset={4}
        trigger={
          <button
            aria-label="Select workspace"
            className="flex w-full min-w-0 items-center justify-between gap-2 rounded-6 py-1 pr-2 pl-1 font-medium text-12 text-neutral-12 outline-none hover:bg-neutral-3 focus:bg-neutral-3 dark:focus:bg-neutral-3 dark:hover:bg-neutral-3"
            type="button"
          >
            <div className="flex min-w-0 items-center gap-2">
              {selectedWorkspace ? (
                <>
                  <WorkspaceAvatar workspace={selectedWorkspace} />
                  <span className="truncate">{selectedWorkspace.name}</span>
                </>
              ) : (
                <>
                  <div className="size-6 shrink-0 animate-pulse rounded bg-neutral-4" />
                  <div className="h-4 w-24 animate-pulse rounded bg-neutral-4" />
                </>
              )}
            </div>
            <ChevronDownIcon className="shrink-0" size="sm" />
          </button>
        }
      >
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            label={workspace.name}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onSelect={() => handleSelect(workspace.id)}
            prefix={<WorkspaceAvatar workspace={workspace} />}
            suffix={workspace.id === selectedWorkspace?.id ? <CheckIcon size="xs" /> : undefined}
          />
        ))}

        {onLogout && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              label="Log out"
              onSelect={onLogout}
              prefix={<LogOutIcon className="ads:text-icon-default text-gray-10" size="sm" />}
            />
          </>
        )}
      </DropdownMenu>
    );
  },
);

WorkspaceSelector.displayName = 'WorkspaceSelector';
